import * as React from 'react';
import {
  HStack,
  View,
  VStack,
  TextInput,
  SegmentedControl,
  SegmentedControlButton,
  Button,
  FormGroup,
  Text,
  Popover,
  PopoverContent,
  ArrowSwooshShareIcon,
  EmailIcon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Note,
  Spacer,
  Link,
  DuplicateFillIcon,
  HideIcon,
  toast,
  CustomizeIcon,
} from '@packages/systems/spring';
import NewWebhookInput from '@packages/shared/render/plugins/Form/FormConnectionsSection/NewWebhookInput/NewWebhookInput';
import {StylePanelLabel} from '@packages/systems/ui/shared-ui/StylePanelLabel';
import {confirm} from '@packages/systems/dialog';
import {ConfiguredOptionsList} from './ConfiguredOptionsList';
import {OptionsMenu} from './OptionsMenu';
import RedirectSection from '@packages/shared/render/plugins/Form/RedirectSection/RedirectSection';
import {type ContextualExpression} from '@packages/utilities/contextual-expression';
import {type ExpressionEditorDispatch} from '@packages/systems/ExpressionEditor-utils';
import {useFormExpressionSettings, useSiteFormDefaults} from '../hooks';
import {WebhookIcon} from '@packages/systems/designer/apps/client';
import {webflowDefaultHost} from '@packages/utilities/environment';
import {EmailNotificationPopover} from '../emailNotifications/emailNotificationPopover';
import type {
  AvailableOption,
  ConfiguredOption,
  FormSettingsPayload,
} from '@packages/systems/designer/forms/types';
import {useWebhookSettings} from '../hooks/useWebhookSettings';
import {useFormSettings} from '../hooks/useFormSettings';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {getSiteUsersSelector} from '@packages/systems/designer/user';
import {getEmailFormTarget} from '@packages/systems/designer/site-data';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {NETWORK_STATUSES} from '@packages/systems/designer/forms/store';
import {trackEvent} from '@packages/utilities/analytics';
import {debounce} from 'lodash';
import {selectSiteShortName} from '@packages/systems/designer-selectors';
import {Expressions, Component} from '@packages/systems/wfdl';
import {FORM_NAME_PATH} from '@packages/shared/render/plugins/Form/constants';
import {
  selectComponents,
  selectCurrentComponentType,
} from '@packages/utilities/platform-selectors';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {
  getName,
  getPluginName,
  pluginName,
} from '@packages/systems/wfdl-Qualified';

const sitePluginName = pluginName('__SitePlugin');
interface ActionMethodProps {
  defaultActionValue?: string;
  defaultMethodValue?: string;
  onSave: (actionValue: string, methodValue: string) => void;
}

const ActionMethodInput: React.FC<ActionMethodProps> = ({
  defaultActionValue,
  defaultMethodValue,
  onSave,
}) => {
  const [actionValue, setActionValue] = React.useState(
    defaultActionValue || ''
  );
  // Default value is 'get' on all new form elements, so checking if defaultActionVAlue
  //and defaultMethodValue, if so use defaultMethodValue, otherwise use 'post'
  const [methodValue, setMethodValue] = React.useState(
    defaultActionValue && defaultMethodValue ? defaultMethodValue : 'post'
  );

  const handleSave = React.useCallback(() => {
    onSave(actionValue, methodValue);
  }, [onSave, actionValue, methodValue]);

  return (
    <VStack spacing={3}>
      <FormGroup label={<Text colorVariant="dimmed">Action</Text>}>
        <TextInput
          value={actionValue}
          onChange={(e) => setActionValue(e.target.value)}
          placeholder="https://webflow.com/action/path"
          type="url"
        />
      </FormGroup>

      <FormGroup label={<Text colorVariant="dimmed">Method</Text>}>
        <SegmentedControl
          value={methodValue}
          onChange={setMethodValue}
          selectionMode="single"
        >
          <SegmentedControlButton value="post">POST</SegmentedControlButton>
          <SegmentedControlButton value="get">GET</SegmentedControlButton>
        </SegmentedControl>
      </FormGroup>

      <HStack spacing={2} justify="end">
        <Button colorVariant="primary" onClick={handleSave}>
          Save
        </Button>
      </HStack>
    </VStack>
  );
};

export interface SendToSectionProps {
  expr: ContextualExpression;
  expressionEditorDispatch: ExpressionEditorDispatch;
  isMiniSettingsOpen: boolean;
  formFormElement: ExpressionElement;
}

const useGetCurrentComponentId = (formElementId: string) => {
  const isFixEnabled = useFeatureFlag(
    FEATURE_FLAGS.SYMBOL_FORM_COMPONENT_ID_FIX
  );
  const componentMap = useDesignerSelector(selectComponents);
  const currentComponentType = useDesignerSelector(selectCurrentComponentType);
  return React.useMemo(() => {
    if (!isFixEnabled) {
      const plugin = getPluginName(currentComponentType);
      if (plugin !== sitePluginName) return null;
      const componentId = getName(currentComponentType);
      return componentId === 'page' ? null : componentId;
    }

    // DEVPL-3905: walk the element tree to find the containing symbol,
    // independent of whether the user has entered the symbol in the canvas.
    let foundComponentId: string | null = null;
    componentMap.forEach((component, componentType) => {
      if (foundComponentId) return;
      const plugin = getPluginName(componentType);
      if (plugin !== sitePluginName) return;

      const componentId = getName(componentType);
      if (componentId === 'page') return;

      if (
        Expressions.getElementById(
          Component.getRender(component),
          formElementId
        )
      ) {
        foundComponentId = componentId;
      }
    });
    return foundComponentId;
  }, [isFixEnabled, componentMap, currentComponentType, formElementId]);
};
export const SendToSection = ({
  expr,
  expressionEditorDispatch,
  isMiniSettingsOpen,
  formFormElement,
}: SendToSectionProps) => {
  // Persisted settings and associated callbacks for form-level settings
  const {actionValue, methodValue, updateActionAndMethod, updateRedirect} =
    useFormExpressionSettings(expr, expressionEditorDispatch);

  const currentComponentId = useGetCurrentComponentId(formFormElement.id);

  const {
    webhooks,
    addWebhook,
    removeWebhook,
    closeSecretModal,
    webhookWithSecret,
  } = useWebhookSettings();

  const defaultEmailTargets = useDesignerSelector(getEmailFormTarget);
  const isFormLevelEmailDisableEnabled = useFeatureFlag(
    FEATURE_FLAGS.FORM_LEVEL_EMAIL_DISABLE
  );
  const isSiteLevelFormDefaultsEnabled = useFeatureFlag(
    FEATURE_FLAGS.SITE_LEVEL_FORM_DEFAULTS
  );

  // Email Notification Popover props
  const {
    formSettings,
    updateState,
    error,
    updateSettings,
    fetchUiState,
    updateUiState,
  } = useFormSettings(formFormElement.id, currentComponentId || undefined);

  const trackSettingEdit = React.useCallback(
    ({
      settingType,
      prev,
      next,
    }: {
      settingType: string;
      prev?: string;
      next?: string;
    }) => {
      trackEvent({
        name: 'Form Settings Edited',
        data: {
          'setting type': settingType,
          'form id': null,
          'element id': formFormElement.id,
          'previous value': prev,
          'new value': next,
        },
      });
    },
    [formFormElement.id]
  );

  const updateFormSettings = React.useCallback(
    (settings: FormSettingsPayload) => {
      trackSettingEdit({
        settingType: 'email notification',
      });
      updateSettings(settings);
    },
    [updateSettings, trackSettingEdit]
  );
  const siteUsers = useDesignerSelector(getSiteUsersSelector);

  const getSiteFormDefaults = useSiteFormDefaults();

  // Get site short name and form name for URL construction
  const siteShortName = useDesignerSelector(selectSiteShortName);
  const formSubmissionsUrl = React.useMemo(() => {
    const formName = Expressions.getElementDataAt(
      formFormElement,
      FORM_NAME_PATH,
      'Text',
      ''
    );
    return `${webflowDefaultHost}/dashboard/sites/${siteShortName}/forms/submissions?name=${encodeURIComponent(
      formName
    )}`;
  }, [siteShortName, formFormElement]);

  // Component UI State
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const {isWebflowConfigured, isEmailConfigured} = fetchUiState;
  // Mount-only seed (preserves the original behavior for isWebflowConfigured).
  // Initial isEmailConfigured value reflects the persisted opt-out when present
  // and the FORM_LEVEL_EMAIL_DISABLE flag is on; otherwise falls back to the
  // pre-feature default. With the flag off, behavior must match dev exactly —
  // do not consult the persisted boolean at all.
  React.useEffect(() => {
    const emailNotificationDisabled = isFormLevelEmailDisableEnabled
      ? formSettings?.emailNotificationsDisabled
      : undefined;
    updateUiState({
      isWebflowConfigured: actionValue.length === 0,
      isEmailConfigured:
        emailNotificationDisabled !== undefined
          ? !emailNotificationDisabled
          : actionValue.length === 0,
    });
  }, []);

  // Reactive update: re-seed isEmailConfigured when the persisted opt-out
  // boolean changes (e.g. after the server confirms a chip toggle round-trip).
  // Gated on FORM_LEVEL_EMAIL_DISABLE so that with the flag off the effect
  // does not call updateUiState in response to data the feature owns.
  React.useEffect(() => {
    if (!isFormLevelEmailDisableEnabled) return;
    const emailNotificationDisabled = formSettings?.emailNotificationsDisabled;
    if (emailNotificationDisabled !== undefined) {
      updateUiState({isEmailConfigured: !emailNotificationDisabled});
    }
  }, [
    isFormLevelEmailDisableEnabled,
    formSettings?.emailNotificationsDisabled,
  ]);

  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [popoverContentToRender, setPopoverContentToRender] = React.useState<
    'ActionMethod' | 'Webhook'
  >('ActionMethod');
  const [isEmailNotificationPopoverOpen, setIsEmailNotificationPopoverOpen] =
    React.useState(false);
  const [anchorRef, setAnchorRef] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    if (updateState === NETWORK_STATUSES.SUCCEEDED) {
      if (!error) {
        setIsEmailNotificationPopoverOpen(false);
      }
    }
  }, [updateState, error]);

  // Callbacks
  const handleOpenPopover = React.useCallback(
    (contentType: 'ActionMethod' | 'Webhook') => {
      setPopoverContentToRender(contentType);
      setIsPopoverOpen(true);
    },
    []
  );

  const handleClosePopover = React.useCallback(() => {
    setIsPopoverOpen(false);
  }, []);

  const handleOpenEmailNotificationPopover = React.useCallback(() => {
    setIsEmailNotificationPopoverOpen(true);
  }, []);

  const handleSecretModalClose = React.useCallback(() => {
    closeSecretModal();
  }, [closeSecretModal]);

  const copyText = React.useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  }, []);

  const handleActionMethodSave = React.useCallback(
    (newActionValue: string, newMethodValue: string) => {
      trackSettingEdit({
        settingType: 'custom action',
        prev: methodValue + actionValue,
        next: newMethodValue + newActionValue,
      });
      updateActionAndMethod(newActionValue, newMethodValue);
      handleClosePopover();
    },
    [
      updateActionAndMethod,
      handleClosePopover,
      trackSettingEdit,
      actionValue,
      methodValue,
    ]
  );

  const handleWebhookSave = React.useCallback(
    (url: string) => {
      trackSettingEdit({settingType: 'add webhook'});
      addWebhook(url);
      handleClosePopover();
    },
    [addWebhook, handleClosePopover, trackSettingEdit]
  );

  const handleMenuOpen = React.useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const handleMenuClose = React.useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const togglePopover = React.useCallback(() => {
    setIsPopoverOpen((prev) => !prev);
  }, []);

  const handleItemDeleted = React.useCallback(
    async (id: string) => {
      let itemType = '';
      if (id === 'webflow') {
        itemType = 'webflow';
        updateUiState({isWebflowConfigured: false});
      } else if (id === 'email') {
        itemType = 'email notification';
        updateUiState({isEmailConfigured: false});
        if (isFormLevelEmailDisableEnabled) {
          updateFormSettings({emailNotificationsDisabled: true});
        }
      } else if (id === 'custom_action') {
        itemType = 'custom action';
        updateActionAndMethod('', 'post');
      } else if (id.startsWith('webhook-')) {
        itemType = 'webhook';
        const webhookUrl = id.replace('webhook-', '');
        const response = await confirm({
          title: 'Webhook deletion',
          content:
            'This webhook will be deleted permanently for all forms on your site. Are you sure?',
          submit: {label: 'Delete', intent: 'danger'},
          cancel: {label: 'Cancel', intent: 'default'},
        });
        if (response.type === 'confirm') {
          removeWebhook(webhookUrl);
        }
      }
      trackSettingEdit({settingType: 'delete option', prev: itemType});
    },
    [
      removeWebhook,
      updateActionAndMethod,
      trackSettingEdit,
      updateUiState,
      updateFormSettings,
      isFormLevelEmailDisableEnabled,
    ]
  );

  // Configured options are options that are already configured in the form
  const configuredOptions: ConfiguredOption[] = React.useMemo(() => {
    const options: ConfiguredOption[] = [];
    if (isWebflowConfigured) {
      options.push({
        id: 'webflow',
        label: 'Webflow',
        iconComponent: <ArrowSwooshShareIcon />,
        onEdit: () => {
          window.open(formSubmissionsUrl, '_blank');
        },
      });
    }

    if (isEmailConfigured) {
      options.push({
        id: 'email',
        label: 'Email Notifications',
        iconComponent: <EmailIcon />,
        onEdit: () => {
          handleOpenEmailNotificationPopover();
        },
      });
    }

    if (actionValue) {
      options.push({
        id: 'custom_action',
        label: actionValue,
        iconComponent: <CustomizeIcon />,
        endLabel: methodValue,
        onEdit: () => {
          handleOpenPopover('ActionMethod');
        },
      });
    }

    const webhookOptions: ConfiguredOption[] = webhooks.map((webhook) => ({
      id: `webhook-${webhook.url}`,
      label: webhook.url,
      iconComponent: <WebhookIcon />,
      onEdit: () => {}, // No-op since webhooks aren't editable
    }));

    return options.concat(webhookOptions);
  }, [
    isWebflowConfigured,
    isEmailConfigured,
    actionValue,
    webhooks,
    handleOpenPopover,
    handleOpenEmailNotificationPopover,
    formSubmissionsUrl,
    methodValue,
  ]);

  // Available options are options that are not already configured in the form but can be added to the form
  const availableOptions: AvailableOption[] = React.useMemo(() => {
    const options: AvailableOption[] = [];
    if (!isWebflowConfigured) {
      options.push({
        id: 'webflow',
        label: 'Webflow',
        iconComponent: <ArrowSwooshShareIcon />,
        onSelect: () => updateUiState({isWebflowConfigured: true}),
        isDisabled: Boolean(actionValue),
        disabledOptionText: 'Remove Custom Action to add this destination',
      });
    }

    if (!isEmailConfigured) {
      options.push({
        id: 'email',
        label: 'Email Notifications',
        iconComponent: <EmailIcon />,
        onSelect: () => {
          updateUiState({isEmailConfigured: true});
          if (isFormLevelEmailDisableEnabled) {
            updateFormSettings({emailNotificationsDisabled: false});
          }
        },
        isDisabled: Boolean(actionValue),
        disabledOptionText: 'Remove Custom Action to add this destination',
      });
    }

    options.push({
      id: 'webhook',
      label: 'Webhook',
      iconComponent: <WebhookIcon />,
      onSelect: () => {
        handleOpenPopover('Webhook');
      },
    });

    if (!actionValue) {
      options.push({
        id: 'custom_action',
        label: 'Custom Action',
        iconComponent: <CustomizeIcon />,
        onSelect: () => {
          handleOpenPopover('ActionMethod');
        },
        isDisabled: isWebflowConfigured || isEmailConfigured,
        disabledOptionText:
          'Remove Webflow and Email Notifications to add this destination',
      });
    }
    return options;
  }, [
    isWebflowConfigured,
    isEmailConfigured,
    actionValue,
    handleOpenPopover,
    updateUiState,
    updateFormSettings,
    isFormLevelEmailDisableEnabled,
  ]);

  // Add debounce because the Tooltip onOpenChange tends to fire twice in a row with the same value
  const trackTooltip = debounce((label: string) => {
    trackEvent({
      name: 'Tooltip Opened',
      data: {
        location: isMiniSettingsOpen ? 'mini settings' : 'settings panel',
        'element id': formFormElement.id,
        topic: label,
      },
    });
  }, 200);

  return (
    <VStack spacing={1} data-automation-id="send-to-section">
      <HStack justify="space-between" align="center" ref={setAnchorRef}>
        <StylePanelLabel>Send to</StylePanelLabel>
        <OptionsMenu
          isOpen={isMenuOpen}
          onOpen={handleMenuOpen}
          onClose={handleMenuClose}
          onTooltipOpen={trackTooltip}
          options={availableOptions}
        />
      </HStack>

      <ConfiguredOptionsList
        options={configuredOptions}
        onItemDeleted={handleItemDeleted}
      />
      <View marginTop="$2">
        <RedirectSection expr={expr} updateRedirect={updateRedirect} />
      </View>

      <Popover
        isOpen={isPopoverOpen}
        onOpenChange={togglePopover}
        anchorRef={anchorRef}
        width="240px"
      >
        <PopoverContent data-automation-id="send-to-section-popover">
          {popoverContentToRender === 'ActionMethod' && (
            <ActionMethodInput
              defaultActionValue={actionValue}
              defaultMethodValue={methodValue}
              onSave={handleActionMethodSave}
            />
          )}
          {popoverContentToRender === 'Webhook' && (
            <NewWebhookInput
              onWebhookSave={handleWebhookSave}
              close={handleClosePopover}
            />
          )}
        </PopoverContent>
      </Popover>

      {/* Email Notification Popover */}
      {formSettings && (
        <EmailNotificationPopover
          isOpen={isEmailNotificationPopoverOpen}
          close={() => setIsEmailNotificationPopoverOpen(false)}
          onOpenChange={setIsEmailNotificationPopoverOpen}
          anchorRef={anchorRef}
          isMiniSettingsOpen={isMiniSettingsOpen}
          formSettings={formSettings}
          updateFormSettings={updateFormSettings}
          siteUsers={siteUsers}
          emailFormTargets={defaultEmailTargets.split(',').map((s) => s.trim())}
          horizontalOffset={20}
          formFormElement={formFormElement}
          getSiteFormDefaults={
            isSiteLevelFormDefaultsEnabled ? getSiteFormDefaults : undefined
          }
        />
      )}

      {/* Secret Key Modal */}
      <Modal
        isOpen={Boolean(webhookWithSecret)}
        onClose={handleSecretModalClose}
        minHeight="200px"
      >
        <ModalHeader title="Webhook added" />
        <ModalBody>
          <VStack isFullHeight>
            <Note
              colorVariant="warning"
              isTinted
              title="Authenticate Webhook Messages"
            >
              <Text colorVariant="primary">
                This key can&lsquo;t be accessed again, copy it to a secure
                location. It can be used to verify webhook requests. Please see
                our documentation on{' '}
                <Link
                  href="https://developers.webflow.com/v2.0.0/data/docs/working-with-webhooks#validating-request-signatures"
                  target="_blank"
                >
                  Validating Request Signatures
                </Link>{' '}
                for information on how to use this key.
              </Text>
            </Note>
            <Spacer />
            <FormGroup label="Secret key">
              <TextInput
                isReadOnly
                value={webhookWithSecret?.secretKey || ''}
                suffix={
                  <Link
                    shouldShowUnderline={false}
                    onClick={() => copyText(webhookWithSecret?.secretKey || '')}
                    suffix={<DuplicateFillIcon colorVariant="blue" />}
                  />
                }
              />
            </FormGroup>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorVariant="primary"
            onClick={handleSecretModalClose}
            prefix={HideIcon}
          >
            Hide key forever
          </Button>
        </ModalFooter>
      </Modal>
    </VStack>
  );
};
